import {
  FLOW_SECTION_META,
  CUSTOM_FLOW_PREFIX,
  FLOW_SECTION_KEYS,
  ROLE_PERSONA_SECTION_KEY,
  DEFAULT_PROMPT_SECTION_ORDER,
  PROMPT_MODE,
} from '../constants/flowSection'

export { ROLE_PERSONA_SECTION_KEY, PROMPT_MODE }

/** 写入 llmsettings.prompt_mode；未明确为 structured 时一律 free（避免 undefined 等误存 structured） */
export function snapshotPromptMode(mode) {
  const s = mode != null ? String(mode).trim().toLowerCase() : ''
  if (s === PROMPT_MODE.STRUCTURED) return PROMPT_MODE.STRUCTURED
  return PROMPT_MODE.FREE
}

/** 客户信息参数默认行（与音色通话 parameter_config 默认一致） */
export const DEFAULT_CUSTOMER_INFO_PARAMS = [
  { paramName: '姓名', paramKey: '{name}' },
  { paramName: '地址', paramKey: '{address}' },
  { paramName: '金额', paramKey: '{money}' },
]

/** 高级参数默认空行（至尊版 / 旗舰版 TTS / 旗舰版 ASR 各一套） */
export const DEFAULT_SPEECH_PARAM_ROW = Object.freeze({ paramName: '', paramKey: '' })

export function createDefaultSpeechParams() {
  return [{ ...DEFAULT_SPEECH_PARAM_ROW }]
}

export function normalizeSpeechParamRows(list) {
  const src = Array.isArray(list) ? list : []
  const rows = src.map((row) => ({
    paramName:
      row && row.paramName != null
        ? String(row.paramName)
        : row && row.name != null
          ? String(row.name)
          : '',
    paramKey:
      row && row.paramKey != null
        ? String(row.paramKey)
        : row && row.key != null
          ? String(row.key)
          : '',
  }))
  return rows.length ? rows : createDefaultSpeechParams()
}

/** 行是否填了参数名或参数值（用于自动展开） */
export function speechParamRowsHaveValues(rows) {
  return (Array.isArray(rows) ? rows : []).some((row) => {
    if (!row || typeof row !== 'object') return false
    const name = row.paramName != null ? String(row.paramName).trim() : ''
    const key = row.paramKey != null ? String(row.paramKey).trim() : ''
    return !!(name || key)
  })
}

function buildParameterConfigBlock(rows, maxParams = 15) {
  const list = speechParamRowsToParameterList(rows)
  return {
    list,
    count: buildSpeechParamCountLabel(list.length, maxParams),
  }
}

/** 表单 speechParams → parameter_config.list 项（name/key） */
export function speechParamRowsToParameterList(rows) {
  return normalizeSpeechParamRows(rows).map((r) => ({
    name: String(r.paramName != null ? r.paramName : '').trim(),
    key: String(r.paramKey != null ? r.paramKey : '').trim(),
  }))
}

export function buildSpeechParamCountLabel(count, max = 15) {
  return `${count}/${max}`
}

/**
 * 高级参数 → 恢复至尊版 / 旗舰版 TTS / 旗舰版 ASR（三套独立）
 * 格式：parameter_config.omni / .tts / .asr
 * 旧格式：仅 tts（与通话模式共用）→ 按当前 presetMode 归入对应一侧，另一侧置默认
 */
export function applySpeechParamsFromParameterConfig(pc, target) {
  if (!pc || typeof pc !== 'object' || !target || typeof target !== 'object') return

  const hasOmni = !!(pc.omni && Array.isArray(pc.omni.list))
  const hasTts = !!(pc.tts && Array.isArray(pc.tts.list))
  const hasAsr = !!(pc.asr && Array.isArray(pc.asr.list))

  if (hasOmni) {
    target.speechParams = normalizeSpeechParamRows(pc.omni.list)
    target.ttsSpeechParams = hasTts
      ? normalizeSpeechParamRows(pc.tts.list)
      : createDefaultSpeechParams()
  } else if (hasTts) {
    // 兼容旧数据：tts.list 曾被至尊版与旗舰版 TTS 共用
    const isCustom = String(target.presetMode || '').trim() === 'custom'
    if (isCustom) {
      target.ttsSpeechParams = normalizeSpeechParamRows(pc.tts.list)
      target.speechParams = createDefaultSpeechParams()
    } else {
      target.speechParams = normalizeSpeechParamRows(pc.tts.list)
      target.ttsSpeechParams = createDefaultSpeechParams()
    }
  }

  if (hasAsr) {
    target.asrSpeechParams = normalizeSpeechParamRows(pc.asr.list)
  }

  if (!Array.isArray(target.speechParams)) {
    target.speechParams = createDefaultSpeechParams()
  }
  if (!Array.isArray(target.ttsSpeechParams)) {
    target.ttsSpeechParams = createDefaultSpeechParams()
  }
  if (!Array.isArray(target.asrSpeechParams)) {
    target.asrSpeechParams = createDefaultSpeechParams()
  }
}

/** 构建 parameter_config 快照（至尊版 omni / 旗舰版 tts / 旗舰版 asr 分表，始终一并写入） */
export function buildParameterConfigSnapshot(options = {}) {
  const {
    speechParams = [],
    ttsSpeechParams = [],
    asrSpeechParams = [],
    maxParams = 15,
  } = options

  return {
    omni: buildParameterConfigBlock(speechParams, maxParams),
    tts: buildParameterConfigBlock(ttsSpeechParams, maxParams),
    asr: buildParameterConfigBlock(asrSpeechParams, maxParams),
  }
}

const CUSTOMER_INFO_PARAMS_MAX = 15

function normalizeCustomerInfoParamRow(row) {
  return {
    paramName:
      row && row.paramName != null
        ? String(row.paramName)
        : row && row.name != null
          ? String(row.name)
          : '',
    paramKey:
      row && row.paramKey != null
        ? String(row.paramKey)
        : row && row.key != null
          ? String(row.key)
          : '',
  }
}

export function createDefaultLittleAssistant() {
  return {
    name: '小助理',
    enabled: false,
    intentLexiconId: '',
    sentences: [],
  }
}

export function createDefaultCustomerInfoParams() {
  return DEFAULT_CUSTOMER_INFO_PARAMS.map((r) => ({ ...r }))
}

/** 客户信息参数 → 插入参数下拉项（token 格式 #显示名称，与 PRD 4.4 一致） */
export function buildCustomerInfoInsertParamOptions(customerInfoParams) {
  const list = Array.isArray(customerInfoParams)
    ? customerInfoParams.map(normalizeCustomerInfoParamRow)
    : []
  return list
    .map((row) => {
      const label = String(row.paramName != null ? row.paramName : '').trim()
      if (!label) return null
      return { label, token: `#${label}` }
    })
    .filter(Boolean)
}

/** 表单 customerInfoParams → llmsettings.customer_info_params */
export function buildCustomerInfoParamsSnapshot(customerInfoParams) {
  const list = Array.isArray(customerInfoParams)
    ? customerInfoParams.map(normalizeCustomerInfoParamRow)
    : createDefaultCustomerInfoParams()
  const n = list.length
  return {
    list: list.map((r) => ({
      name: String(r.paramName != null ? r.paramName : '').trim(),
      key: String(r.paramKey != null ? r.paramKey : '').trim(),
    })),
    count: `${n}/${CUSTOMER_INFO_PARAMS_MAX}`,
  }
}

/** llmsettings.call_control_settings.customer_info_params → 表单 */
export function applyCustomerInfoParamsFromSnapshot(raw) {
  if (raw && Array.isArray(raw.list)) {
    return raw.list.map(normalizeCustomerInfoParamRow)
  }
  return createDefaultCustomerInfoParams()
}

/** 小助理 → llmsettings.assistant_settings（意图词库 id 与 FormItemShareKeyword.ref 一致） */
export function buildLittleAssistantSnapshot(littleAssistant) {
  const a =
    littleAssistant && typeof littleAssistant === 'object'
      ? littleAssistant
      : createDefaultLittleAssistant()
  const sentenceList = Array.isArray(a.sentences) ? a.sentences : []
  return {
    name: a.name != null && String(a.name).trim() !== '' ? String(a.name).trim() : '小助理',
    enabled: !!a.enabled,
    intent_ref: {
      id: a.intentLexiconId != null ? String(a.intentLexiconId) : '',
      is_priority: true,
    },
    sentences: sentenceList.map((k) => String(k)),
  }
}

/** call_control_settings.assistant_settings → 表单小助理 */
export function applyLittleAssistantFromSettings(assistantSettings) {
  const la =
    assistantSettings && typeof assistantSettings === 'object' ? assistantSettings : null
  if (!la) return createDefaultLittleAssistant()
  let intentLexiconId = ''
  if (la.intent_ref && la.intent_ref.id != null) {
    intentLexiconId = String(la.intent_ref.id)
  }
  const sentenceList = Array.isArray(la.sentences) ? la.sentences : []
  return {
    name: la.name != null && String(la.name).trim() !== '' ? String(la.name).trim() : '小助理',
    enabled: !!la.enabled,
    intentLexiconId,
    sentences: sentenceList.map((k) => String(k)),
  }
}

function buildFixedTitleToKeyMap() {
  const map = {}
  FLOW_SECTION_KEYS.forEach((k) => {
    const meta = FLOW_SECTION_META[k]
    if (!meta) return
    map[meta.title] = k
    if (meta.promptTitle) map[meta.promptTitle] = k
  })
  return map
}

/**
 * 从 llmsettings.business_flow 恢复业务 Tab 表单、区块顺序与自定义块
 * @returns {{ customFlowSections: array, flowSectionOrder: array, formPatch: object }}
 */
export function applyBusinessFlowFromLlmsettings(businessFlow, prevCustomFlowSections = []) {
  const formPatch = {}
  if (!Array.isArray(businessFlow) || !businessFlow.length) {
    return {
      customFlowSections: [],
      flowSectionOrder: [...DEFAULT_PROMPT_SECTION_ORDER],
      formPatch,
    }
  }

  const fixedTitleToKey = buildFixedTitleToKeyMap()
  const customSections = []
  const order = []

  businessFlow.forEach((item, index) => {
    const title = String(item && item.title != null ? item.title : '').trim()
    const content = item && item.content != null ? String(item.content) : ''
    if (!title) return

    const fixedKey = fixedTitleToKey[title]
    if (fixedKey) {
      order.push(fixedKey)
      formPatch[fixedKey] = content
      return
    }

    const prevCustom = (prevCustomFlowSections || []).find(
      (c) => String(c.title || '').trim() === title,
    )
    const id = (prevCustom && prevCustom.id) || `cf_${Date.now()}_${index}`
    customSections.push({
      id: String(id),
      title,
      body: content,
    })
    order.push(`${CUSTOM_FLOW_PREFIX}${id}`)
  })

  for (const fk of FLOW_SECTION_KEYS) {
    if (!order.includes(fk)) order.push(fk)
  }
  customSections.forEach((c) => {
    const ck = `${CUSTOM_FLOW_PREFIX}${c.id}`
    if (!order.includes(ck)) order.push(ck)
  })

  return {
    customFlowSections: customSections,
    flowSectionOrder: normalizePromptSectionOrder(order, customSections),
    formPatch,
  }
}

/**
 * 从旧版扁平 llmsettings 字段组装 business_flow 持久化块
 */
export function buildBusinessFlowDocumentFromLegacyForm(ctx) {
  const {
    flowSectionOrder,
    customFlowSections = [],
    flowSectionTitles = {},
    fieldValues = {},
    intentTagRules = [],
  } = ctx || {}

  const customRows = Array.isArray(customFlowSections) ? customFlowSections : []
  const customIds = new Set(customRows.map((c) => String((c && c.id) || '')))
  const setFixed = new Set(FLOW_SECTION_KEYS)

  const validOrderKey = (k) => {
    if (k === ROLE_PERSONA_SECTION_KEY) return false
    if (typeof k !== 'string') return false
    if (k.indexOf(CUSTOM_FLOW_PREFIX) === 0) {
      return customIds.has(k.slice(CUSTOM_FLOW_PREFIX.length))
    }
    return setFixed.has(k)
  }

  let order = Array.isArray(flowSectionOrder) ? flowSectionOrder.filter(validOrderKey) : []
  if (!order.length) order = [...DEFAULT_PROMPT_SECTION_ORDER]
  for (const fk of FLOW_SECTION_KEYS) {
    if (!order.includes(fk)) order.push(fk)
  }
  customRows.forEach((c) => {
    const id = String((c && c.id) || '')
    if (!id) return
    const ck = `${CUSTOM_FLOW_PREFIX}${id}`
    if (!order.includes(ck)) order.push(ck)
  })

  const blocks = []
  order.forEach((k) => {
    if (k === ROLE_PERSONA_SECTION_KEY) return
    if (k.indexOf(CUSTOM_FLOW_PREFIX) === 0) {
      const id = k.slice(CUSTOM_FLOW_PREFIX.length)
      const row = customRows.find((r) => String((r && r.id) || '') === id)
      const title = row && String(row.title || '').trim() ? String(row.title).trim() : '自定义'
      const content = row && row.body != null ? String(row.body) : ''
      if (String(content).trim()) blocks.push({ title, content })
      return
    }
    if (k === 'intentTag') {
      const text = (intentTagRules || [])
        .filter(
          (r) =>
            (r && r.name && String(r.name).trim()) || (r && r.rule && String(r.rule).trim()),
        )
        .map(
          (r) =>
            `${String((r && r.name) || '').trim()}：${String((r && r.rule) || '').trim()}`,
        )
        .join('\n')
      if (String(text).trim()) {
        blocks.push({
          title: getFixedSectionTitle(flowSectionTitles, k),
          content: text,
        })
      }
      return
    }
    const raw = fieldValues[k]
    const content = raw != null ? String(raw) : ''
    if (!String(content).trim()) return
    blocks.push({
      title: getFixedSectionTitle(flowSectionTitles, k),
      content,
    })
  })
  return blocks
}

const LEGACY_FLAT_LLMSETTINGS_TO_FORM = {
  opening: 'opening',
  business_background: 'businessBackground',
  business_target: 'businessTarget',
  business_process: 'businessProcess',
  dialogue_example: 'dialogueExample',
  dialogue_constraint: 'dialogueConstraint',
  question_constraint: 'questionConstraint',
}

/** 旧版扁平 llmsettings 顶层 snake 业务字段 → business_flow（仅 llmsettings，不含 settings.llm） */
export function buildBusinessFlowFromLegacyFlatLlmsettings(src) {
  const raw = src && typeof src === 'object' ? src : {}
  const fieldValues = {}
  Object.keys(LEGACY_FLAT_LLMSETTINGS_TO_FORM).forEach((legacyKey) => {
    if (raw[legacyKey] === undefined) return
    fieldValues[LEGACY_FLAT_LLMSETTINGS_TO_FORM[legacyKey]] =
      raw[legacyKey] != null ? String(raw[legacyKey]) : ''
  })

  let customFlowSections = []
  if (Array.isArray(raw.custom_flow_sections)) {
    customFlowSections = raw.custom_flow_sections
      .map((x) => ({
        id: String((x && x.id) || ''),
        title: x && x.title != null ? String(x.title) : '',
        body: x && x.body != null ? String(x.body) : '',
      }))
      .filter((x) => x.id)
  }

  const customIds = new Set(customFlowSections.map((c) => c.id))
  const setFixed = new Set(FLOW_SECTION_KEYS)
  const validOrderKey = (k) => {
    if (typeof k !== 'string') return false
    if (k.indexOf(CUSTOM_FLOW_PREFIX) === 0) {
      return customIds.has(k.slice(CUSTOM_FLOW_PREFIX.length))
    }
    return setFixed.has(k)
  }

  let flowSectionOrder = Array.isArray(raw.flow_section_order)
    ? raw.flow_section_order.filter(validOrderKey)
    : []
  if (!flowSectionOrder.length) flowSectionOrder = [...DEFAULT_PROMPT_SECTION_ORDER]

  const flowSectionTitles = {}
  if (raw.flow_section_titles && typeof raw.flow_section_titles === 'object') {
    FLOW_SECTION_KEYS.forEach((k) => {
      const val = raw.flow_section_titles[k]
      if (val != null && String(val).trim() !== '') flowSectionTitles[k] = String(val).trim()
    })
  }

  return buildBusinessFlowDocumentFromLegacyForm({
    flowSectionOrder,
    customFlowSections,
    flowSectionTitles,
    fieldValues,
    intentTagRules: [],
  })
}

export function isCustomFlowKey(sectionKey) {
  return typeof sectionKey === 'string' && sectionKey.indexOf(CUSTOM_FLOW_PREFIX) === 0
}

export function isRolePersonaSectionKey(sectionKey) {
  return sectionKey === ROLE_PERSONA_SECTION_KEY
}

/** 业务块顺序键是否有效（含 rolePersona、固定块、自定义块） */
export function isValidPromptSectionOrderKey(sectionKey, customFlowSections = []) {
  if (isRolePersonaSectionKey(sectionKey)) return true
  if (isCustomFlowKey(sectionKey)) {
    const id = sectionKey.slice(CUSTOM_FLOW_PREFIX.length)
    return (customFlowSections || []).some((c) => c.id === id)
  }
  return FLOW_SECTION_KEYS.includes(sectionKey)
}

/** 规范化提示词区块顺序：保证 rolePersona 在首位、固定块不丢失 */
export function normalizePromptSectionOrder(order, customFlowSections = []) {
  const customIds = new Set((customFlowSections || []).map((c) => c.id))
  const validKey = (k) => isValidPromptSectionOrderKey(k, customFlowSections)

  let next = Array.isArray(order) ? order.filter(validKey) : []
  if (!next.length) next = [...DEFAULT_PROMPT_SECTION_ORDER]

  if (!next.includes(ROLE_PERSONA_SECTION_KEY)) {
    next.unshift(ROLE_PERSONA_SECTION_KEY)
  } else {
    next = [ROLE_PERSONA_SECTION_KEY, ...next.filter((k) => k !== ROLE_PERSONA_SECTION_KEY)]
  }

  FLOW_SECTION_KEYS.forEach((fk) => {
    if (!next.includes(fk)) next.push(fk)
  })
  ;(customFlowSections || []).forEach((c) => {
    const ck = `${CUSTOM_FLOW_PREFIX}${c.id}`
    if (customIds.has(c.id) && !next.includes(ck)) next.push(ck)
  })
  return next
}

export function getCustomRow(customFlowSections, sectionKey) {
  if (!isCustomFlowKey(sectionKey)) return null
  const id = sectionKey.slice(CUSTOM_FLOW_PREFIX.length)
  return customFlowSections.find((c) => c.id === id) || null
}

export function getFixedSectionTitle(flowSectionTitles, key) {
  const meta = FLOW_SECTION_META[key]
  if (!meta) return ''
  const o = flowSectionTitles[key]
  if (o != null && String(o).trim() !== '') return String(o).trim()
  return meta.title
}

export function getFlowSectionPromptValue(form, intentTag, key) {
  if (key === 'intentTag') {
    return (intentTag.rules || [])
      .filter(
        (r) =>
          (r && r.name && String(r.name).trim()) || (r && r.rule && String(r.rule).trim()),
      )
      .map(
        (r) =>
          `${String((r && r.name) || '').trim()}：${String((r && r.rule) || '').trim()}`,
      )
      .join('\n')
  }
  return form[key]
}
